import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useHistory } from "react-router-dom";

import { GooglePayPaymentMethod, GooglePayPaymentResponse } from "./types";
import { IBreachDetails } from "../../../models/breach-details.interface";
import { useBrowserData } from "../hooks/useBrowserData";
import { usePaymentSession } from "../hooks/usePaymentSession";

type GooglePayContextType = {
  handleGooglePayClick: (
    amount: number,
    breachDetails: IBreachDetails,
    email: string,
    setError: (message: string) => void,
    setGooglePayLoading: (loading: boolean) => void
  ) => (data: google.payments.api.PaymentData) => void;
  allowedPaymentMethods: Array<GooglePayPaymentMethod>;
  getPaymentDataRequest: (
    amount: number
  ) => google.payments.api.PaymentDataRequest;
};

const allowedCardAuthMethods =
  (process.env.REACT_APP_GOOGLE_PAY_ALLOWED_CARD_AUTH_METHODS?.split(",") || [
    "PAN_ONLY",
    "CRYPTOGRAM_3DS",
  ]) as Array<"PAN_ONLY" | "CRYPTOGRAM_3DS">;

export const GooglePayContext = createContext<GooglePayContextType>({
  handleGooglePayClick: () => () => {},
  allowedPaymentMethods: [],
  getPaymentDataRequest: () => ({} as google.payments.api.PaymentDataRequest),
});

export const GooglePayProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const history = useHistory();
  const { paymentProduct, session } = usePaymentSession();
  const browserData = useBrowserData();

  const [allowedPaymentMethods, setAllowedPaymentMethods] = useState<
    Array<GooglePayPaymentMethod>
  >([]);

  useEffect(() => {
    if (paymentProduct) {
      const { networks = [], gateway = "" } =
        paymentProduct.json.paymentProduct320SpecificData || {};
      setAllowedPaymentMethods([
        {
          type: "CARD",
          parameters: {
            allowedAuthMethods: allowedCardAuthMethods,
            allowedCardNetworks: networks as Array<
              "AMEX" | "MASTERCARD" | "VISA"
            >,
          },
          tokenizationSpecification: {
            type: "PAYMENT_GATEWAY",
            parameters: {
              gateway,
              gatewayMerchantId:
                process.env.REACT_APP_GOOGLE_PAY_GATEWAY_MERCHANT_ID || "",
            },
          },
        },
      ]);
    }
  }, [paymentProduct]);

  const getPaymentDataRequest = useCallback(
    (amount: number): google.payments.api.PaymentDataRequest => {
      return {
        apiVersion: 2,
        apiVersionMinor: 0,
        allowedPaymentMethods: allowedPaymentMethods,
        merchantInfo: {
          merchantId: process.env.REACT_APP_GOOGLE_PAY_MERCHANT_ID || "",
          merchantName: process.env.REACT_APP_GOOGLE_PAY_MERCHANT_NAME || "",
        },
        transactionInfo: {
          totalPriceStatus: "FINAL",
          totalPriceLabel: "Total",
          totalPrice: amount.toFixed(2),
          currencyCode: process.env.REACT_APP_GOOGLE_PAY_CURRENCY_CODE || "",
          countryCode: process.env.REACT_APP_GOOGLE_PAY_COUNTRY_CODE || "",
        },
      };
    },
    [allowedPaymentMethods]
  );

  const handleGooglePayClick = useCallback(
    (
        amount: number,
        breachDetails: IBreachDetails,
        email: string,
        setError: (message: string) => void,
        setGooglePayLoading: (loading: boolean) => void
      ) =>
      async (data: google.payments.api.PaymentData) => {
        try {
          if (paymentProduct && session) {
            setGooglePayLoading(true);
            const response = await fetch(
              (process.env.REACT_APP_API_URL || "") + "/google-pay/payment",
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({
                  amount,
                  paymentData: data.paymentMethodData.tokenizationData.token,
                  breachDetails: {
                    amount: breachDetails.amount,
                    ticketNbr: breachDetails.ticketNbr,
                    vrm: breachDetails.vrm,
                    siteName: breachDetails.location,
                  },
                  browserData,
                  email,
                }),
              }
            );
            const responseData: GooglePayPaymentResponse =
              await response.json();
            setGooglePayLoading(false);
            if (responseData.error) {
              setError("Payment processing error");
              history.push(`/failed/?errorMessage=${responseData.message}`);
              return;
            }
            if (responseData.redirectUrl) {
              window.location.href = responseData.redirectUrl;
              return;
            }
            if (!responseData.error) {
              history.push(
                `/confirmation/${breachDetails.ticketNbr}/${breachDetails.vrm}`
              );
              return;
            }
          }
        } catch (error) {
          setError("Payment processing error");
          setGooglePayLoading(false);
        }
      },
    [paymentProduct, browserData]
  );

  return (
    <GooglePayContext.Provider
      value={{
        handleGooglePayClick,
        allowedPaymentMethods,
        getPaymentDataRequest,
      }}
    >
      {children}
    </GooglePayContext.Provider>
  );
};

export const useGooglePay = () => {
  return useContext(GooglePayContext);
};
